import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bookingActions } from "../../../../store/reducers/booking";
import { uiActions } from "../../../../store/reducers/ui";

import constants from "../../../../utils/constants";
import useApi from "../../../../hooks/useApi";
import DatePicker from "../../../UI/DatePicker";
import OtherOccupantsForm from "./OtherOccupantsForm";

const checkValidity = (clientAdditionalInfo, preQualified) => {
  let accidentValid = true;

  if (!preQualified && (!clientAdditionalInfo.accidentDate || !clientAdditionalInfo.description)) {
    accidentValid = false;
  }

  let validInfo =
    clientAdditionalInfo.address &&
    accidentValid &&
    clientAdditionalInfo.numberOfOccupants &&
    clientAdditionalInfo.driverOrPassenger;

  if (validInfo) {
    Object.values(clientAdditionalInfo.otherOccupants).forEach((otherOccupant) => {
      if (!validInfo) {
        return;
      }

      if (
        !otherOccupant.firstName ||
        !otherOccupant.lastName ||
        !otherOccupant.sameAddress ||
        !otherOccupant.isMinor ||
        !otherOccupant.driverOrPassenger ||
        !otherOccupant.attendingConsult
      ) {
        return (validInfo = false);
      }

      if (
        (otherOccupant.sameAddress === "no" && !otherOccupant.address) ||
        (otherOccupant.isMinor === "yes" && !otherOccupant.dateOfBirth) ||
        (otherOccupant.driverOrPassenger === "other" && !otherOccupant.driverPassengerOther)
      ) {
        return (validInfo = false);
      }
    });
  }

  return validInfo;
};

const generateUpdateObject = (prevState, objectToUpdate, property, value, extraProperty, extraValue) => {
  return {
    ...prevState,
    otherOccupants: {
      ...prevState.otherOccupants,
      [objectToUpdate]: { ...prevState.otherOccupants[objectToUpdate], [property]: value, [extraProperty]: extraValue },
    },
  };
};

export default function OtherOccupants() {
  const initialClientInfo = useSelector((state) => state.booking.clientDetails);
  const preQualified =
    useSelector((state) => state.booking.bookingStatus) === constants.BOOKING_STATUS.EXTRA_OCCUPANTS_QUALIFIED;

  const dispatch = useDispatch();

  const [clientAdditionalInfo, setClientDetails] = useState(initialClientInfo.additionalInfo);
  const [datePickerOpen, setDatePickerOpen] = useState({ open: false });
  const [activeDatePicker, setActiveDatePicker] = useState();
  const [validInfo, setValidInfo] = useState(false);
  const occupantsTop = useRef();

  const { isLoading: updatingConsultRequest, sendRequest: updateConsultRequest } = useApi({
    successMessage: "Consult request updated successfully",
    errorMessage: "Error updating consult request",
  });

  const otherOccupants = clientAdditionalInfo.otherOccupants;

  const clientAdditionalInfoChangeHandler = (otherOccupant, event) => {
    const name = event.target.name;
    const value = event.target.value;

    if (name === "isMinor") {
      setClientDetails((prevState) => generateUpdateObject(prevState, otherOccupant, name, value, "dateOfBirth", ""));
    } else if (name === "driverOrPassenger") {
      setClientDetails((prevState) =>
        generateUpdateObject(prevState, otherOccupant, name, value, "driverPassengerOther", "")
      );
    } else {
      setClientDetails((prevState) => generateUpdateObject(prevState, otherOccupant, name, value));
    }
  };

  const dateOfBirthChangeHandler = (date) =>
    setClientDetails((prevState) => generateUpdateObject(prevState, activeDatePicker, "dateOfBirth", date.toISO()));

  const handleDatePickerOpen = (otherOccupant) => {
    const dateOfBirth = otherOccupants[otherOccupant].dateOfBirth;

    //Need this to work with TAB key. Otherwise there is no way to close the dialog since the input is on focus while the dialog is open
    document.getElementsByName("dateOfBirth")[0].blur();

    setActiveDatePicker(otherOccupant);
    setDatePickerOpen((prevState) => ({ ...prevState, dateOfBirth, open: true }));
  };

  const handleDatePickerClosed = () => {
    setDatePickerOpen((prevState) => ({ ...prevState, open: false }));
  };

  const handleCancelDialogOpen = () => {
    dispatch(uiActions.updateCancelDialogOpen(true));
  };

  const bookingUpdateCallback = (data) => {
    const result = data.result;

    if (result === "success") {
      dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.COMPLETE_EXTRA_OCCUPANTS));
    }
  };

  const confirmOtherOccupantsDetails = (event) => {
    event.preventDefault();

    //Double checking since there is a 500ms delay between each time the user updates an input and it is possible to click submit
    const validInfo = checkValidity(clientAdditionalInfo, preQualified);

    const payload = {
      consultId: initialClientInfo.meetingId,
      selectedBranch: initialClientInfo.selectedBranch,
      caseKey: initialClientInfo.caseKey,
      firstName: initialClientInfo.firstName,
      fullName: initialClientInfo.firstName + " " + initialClientInfo.middleName + " " + initialClientInfo.lastName,
      consultDateTime: initialClientInfo.meetingDateTime,
      clientAdditionalInfo,
      source: initialClientInfo.source,
    };

    if (validInfo) {
      updateConsultRequest(
        {
          url: "api/booking-app/consult-update.php",
          method: "POST",
          payload,
        },
        bookingUpdateCallback
      );
    }
  };

  //Seeting a 500ms delay between input changes. Otherwise the validity check will trigger every time the user types a character
  useEffect(() => {
    const timer = setTimeout(() => {
      setValidInfo(checkValidity(clientAdditionalInfo, preQualified));
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [clientAdditionalInfo, preQualified]);

  useEffect(() => {
    occupantsTop.current?.scrollIntoView();
  }, []);

  return (
    <>
      <div ref={occupantsTop}></div>

      <OtherOccupantsForm
        otherOccupants={otherOccupants}
        onSubmit={confirmOtherOccupantsDetails}
        onInfoChangeHandler={clientAdditionalInfoChangeHandler}
        onDatePickerOpen={handleDatePickerOpen}
        loading={updatingConsultRequest}
        validInfo={validInfo}
        onClose={handleCancelDialogOpen}
      />

      {datePickerOpen.open && (
        <DatePicker
          title="Date of Birth"
          open={datePickerOpen.open}
          onClose={handleDatePickerClosed}
          onDateSelected={dateOfBirthChangeHandler}
          selectedDate={datePickerOpen.dateOfBirth}
          yearsBack={17}
          tooltipText="Must be within 17 years"
        />
      )}
    </>
  );
}
